/**
 * Workspaces API client; response shapes match WorkspaceContext.
 */

import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { kongBrowserFetchUrl } from "@/lib/kongApi";
import { getTenantApiWorkspaceId, tenantApiFetch, tenantDashboardPath } from "@/lib/tenantApi";

export function normalizeWorkspaceEmail(email) {
  return String(email || "")
    .trim()
    .toLowerCase();
}

/**
 * @param {{ userEmail: string }} params — userEmail ignored; identity comes from Bearer.
 * @returns {Promise<{ data: { workspaces: Array<{ id: string, name: string, slug: string, businessUnit?: string }> } }>}
 */
export async function listWorkspacesForUser({ userEmail: _userEmail }) {
  const data = await tenantApiFetch(tenantDashboardPath("/workspaces"));
  const list = (data.workspaces || []).map((w) => ({
    id: w.id,
    name: w.name,
    slug: w.slug,
    businessUnit: w.businessUnit || undefined,
    supportUrl: w.supportUrl ?? undefined,
    directedFromUrl: w.directedFromUrl ?? undefined,
    logoUrl: w.logoUrl ?? null,
  }));
  return { data: { workspaces: list } };
}

/**
 * @param {{ userEmail: string }} params
 * @returns {Promise<{ data: { workspaceId: string | null } }>}
 */
export async function getActiveWorkspaceId({ userEmail: _userEmail }) {
  const data = await tenantApiFetch(tenantDashboardPath("/workspaces/active"));
  return { data: { workspaceId: data.workspaceId ?? null } };
}

/**
 * @param {{ userEmail: string, workspaceId: string | null }} params
 */
export async function setActiveWorkspaceId({ userEmail: _userEmail, workspaceId }) {
  if (workspaceId == null) {
    return { data: { ok: true } };
  }
  await tenantApiFetch(tenantDashboardPath("/workspaces/active"), {
    method: "POST",
    body: JSON.stringify({ workspaceId }),
  });
  return { data: { ok: true } };
}

/**
 * @param {{ workspaceId: string }} params
 * @returns {Promise<{ data: { emails: string[] } }>}
 */
export async function listWorkspaceMemberEmails({ workspaceId }) {
  const data = await tenantApiFetch(tenantDashboardPath(`/workspaces/${encodeURIComponent(workspaceId)}/members`));
  return { data: { emails: data.emails || [] } };
}

/**
 * @param {{ workspaceId: string, userEmails: string[] }} params
 */
export async function setWorkspaceMembers({ workspaceId, userEmails }) {
  const data = await tenantApiFetch(tenantDashboardPath(`/workspaces/${encodeURIComponent(workspaceId)}/members`), {
    method: "PUT",
    body: JSON.stringify({ userEmails: userEmails || [] }),
  });
  return { data: { ok: true, members: data.members ?? { provisioned: [], invited: [], failed: [] } } };
}

/**
 * @param {{ userEmail: string, name: string, businessUnit?: string, memberEmails?: string[] }} params
 */
export async function createWorkspace({
  userEmail: _userEmail,
  name,
  businessUnit,
  memberEmails = [],
  inviteEmails = [],
  supportUrl,
  directedFromUrl,
}) {
  const data = await tenantApiFetch(tenantDashboardPath("/workspaces"), {
    method: "POST",
    body: JSON.stringify({
      name,
      businessUnit: businessUnit ?? null,
      memberEmails: memberEmails ?? [],
      inviteEmails: inviteEmails ?? [],
      supportUrl: supportUrl ?? null,
      directedFromUrl: directedFromUrl ?? null,
    }),
  });
  const w = data.workspace;
  return {
    data: {
      workspace: {
        id: w.id,
        name: w.name,
        slug: w.slug,
        businessUnit: w.businessUnit || undefined,
        supportUrl: w.supportUrl ?? undefined,
        directedFromUrl: w.directedFromUrl ?? undefined,
      },
      members: data.members ?? { provisioned: [], invited: [], failed: [] },
    },
  };
}

/**
 * @param {{ workspaceId: string }} params
 */
export async function deleteWorkspace({ workspaceId }) {
  await tenantApiFetch(tenantDashboardPath(`/workspaces/${encodeURIComponent(workspaceId)}`), {
    method: "DELETE",
  });
  return { data: { ok: true } };
}

/**
 * @param {{ workspaceId: string, name?: string, businessUnit?: string | null }} params
 */
export async function updateWorkspace({
  workspaceId,
  name,
  businessUnit,
  supportUrl,
  directedFromUrl,
}) {
  const body = {};
  if (name != null) body.name = name;
  if (businessUnit !== undefined) body.businessUnit = businessUnit;
  if (supportUrl !== undefined) body.supportUrl = supportUrl;
  if (directedFromUrl !== undefined) body.directedFromUrl = directedFromUrl;
  const data = await tenantApiFetch(tenantDashboardPath(`/workspaces/${encodeURIComponent(workspaceId)}`), {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  const w = data.workspace;
  return {
    data: {
      workspace: {
        id: w.id,
        name: w.name,
        slug: w.slug,
        businessUnit: w.businessUnit || undefined,
        supportUrl: w.supportUrl ?? undefined,
        directedFromUrl: w.directedFromUrl ?? undefined,
      },
    },
  };
}

/** Allowed workspace-icon upload types and size cap (mirrors backend validation). */
export const WORKSPACE_LOGO_ACCEPT = "image/png,image/jpeg";
export const WORKSPACE_LOGO_MAX_BYTES = 2 * 1024 * 1024;

async function _parseLogoResponse(res) {
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  if (!res.ok) {
    const detail = data?.detail;
    const msg =
      (detail && typeof detail === "object" && detail.message) ||
      (detail && typeof detail === "object" && detail.error) ||
      (typeof detail === "string" ? detail : null) ||
      data?.message ||
      res.statusText;
    const err = new Error(typeof msg === "string" ? msg : "Upload failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Upload a workspace icon (PNG/JPEG). Multipart, so it bypasses the JSON fetch
 * wrapper (Content-Type must be set by the browser with the multipart boundary).
 * @param {{ workspaceId: string, file: File }} params
 * @returns {Promise<{ logoUrl: string | null }>}
 */
export async function uploadWorkspaceLogo({ workspaceId, file }) {
  const form = new FormData();
  form.append("file", file);
  const headers = { Accept: "application/json", ...getBearerAuthHeaders() };
  const wid = getTenantApiWorkspaceId();
  if (wid) headers["X-Workspace-Id"] = wid;
  const res = await fetchWithTimeout(
    kongBrowserFetchUrl(tenantDashboardPath(`/workspaces/${encodeURIComponent(workspaceId)}/logo`)),
    { method: "POST", headers, body: form }
  );
  const data = await _parseLogoResponse(res);
  return { logoUrl: data?.logoUrl ?? null };
}

/**
 * Remove a workspace icon.
 * @param {{ workspaceId: string }} params
 */
export async function deleteWorkspaceLogo({ workspaceId }) {
  await tenantApiFetch(tenantDashboardPath(`/workspaces/${encodeURIComponent(workspaceId)}/logo`), {
    method: "DELETE",
  });
  return { logoUrl: null };
}
